import { useShadcnNotification } from '@src/components/ui/notification';
import {
    PreviewDocument,
    PreviewDocumentRef,
} from '@src/features/DocumentSign/PreviewDocument';
import {
    SignFlow,
    SignFlowRef,
    SignFlowTriggerData,
    type MobileSignUrls,
} from '@src/features/DocumentSign/SignFlow';
import LayoutView from '@src/features/LayoutViewExpose';
import { LayoutViewVertical } from '@src/features/LayoutViewExpose/LayoutViewVertical';
import {
    ServiceButtonAction,
    useServiceBtnViewer,
} from '@src/features/useServiceBtnViewer';
import { useEgovMobileSignCheckProgress } from '@src/pages/Services/hooks/useEgovmobileSignCheckProgress';

import useLangText from '@shared/hooks/useLangText';
import { ProcessFieldRaw } from '@shared/types/process';
import { omitHiddenDependentLayoutFieldValues } from '@shared/util/layoutDependentFields';
import { makeTree } from '@shared/util/makeTree';
import {
    decideSignFromForm,
    formValuesAfterPreparationSaved,
    readSignPreparationFields,
} from '@shared/util/signPreparation';

import {
    applyServerValidationErrors,
    WebComponentServerValidationError,
} from './serverValidationErrors';

import { ShadcnForm } from '@shadcn-features/core';
import { getQueryKey } from '@shadcn-features/query';
import { useQueryClient } from '@tanstack/react-query';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type SubmitAction =
    | (ServiceButtonAction & {
          extraValues?: Record<string, unknown>;
          extraFields?: Record<string, unknown>;
      })
    | {
          buttonId: string;
          extraValues?: Record<string, unknown>;
          extraFields?: Record<string, unknown>;
      };

type LayoutVerticalPreviewWebComponentsProps = Omit<
    React.ComponentPropsWithoutRef<typeof LayoutView>,
    'childrenBtnForFlowTab'
> & {
    mobileSignUrls?: MobileSignUrls;
    serverValidationErrors?: WebComponentServerValidationError[];
};

export const LayoutVerticalPreviewWebComponents = ({
    layoutProcess,
    service,
    tabProps,
    fileUploadController,
    mobileSignUrls,
    serverValidationErrors,
}: LayoutVerticalPreviewWebComponentsProps) => {
    const { t } = useTranslation();
    const getLangText = useLangText();

    const refDiv = useRef<HTMLDivElement>(null);

    const [form, setForm] = useState<UseFormReturn>();
    const handleForm = useCallback((nextForm: UseFormReturn) => {
        setForm((currentForm) =>
            currentForm === nextForm ? currentForm : nextForm,
        );
    }, []);

    const signRef =
        useRef<SignFlowRef<ServiceButtonAction & SignFlowTriggerData>>(null);
    const previewDocRef = useRef<PreviewDocumentRef>(null);

    const notification = useShadcnNotification();

    const { autoMakeBtns } = useServiceBtnViewer({
        buttons: service?.buttons ?? [],
        service: service,
    });
    const { removeMobileSignSessionId } = useEgovMobileSignCheckProgress({
        signRef: signRef,
    });

    const layoutFieldIds = useMemo(() => {
        const fieldIds: string[] = [];
        layoutProcess.blocks?.forEach((block) => {
            if (block.type === 'Field') {
                fieldIds.push(block.options.processFieldId);
            }
        });

        return fieldIds;
    }, [layoutProcess.blocks]);

    const queryClient = useQueryClient();
    const serverErrorFieldNamesRef = useRef<string[]>([]);

    const getFieldLabel = useCallback(
        (fieldId: string) => {
            const cachedField = queryClient.getQueryData<ProcessFieldRaw>(
                getQueryKey({
                    url: `flow/ServiceProcessFields/Get/${fieldId}`,
                }),
            );

            return cachedField ? getLangText(cachedField.name) : undefined;
        },
        [getLangText, queryClient],
    );

    const validateForm = useCallback(async () => {
        const isValid = await form?.trigger();

        if (form && isValid) {
            return omitHiddenDependentLayoutFieldValues(
                form.getValues(),
                makeTree(layoutProcess.blocks ?? []),
            );
        } else {
            const firstErrorFieldId = layoutFieldIds.find(
                (fieldId) => form?.getFieldState(fieldId).error,
            );
            const firstErrorFieldLabel = firstErrorFieldId
                ? getFieldLabel(firstErrorFieldId)
                : null;

            notification.error({
                message: t('translation:validationRequired'),
                description:
                    firstErrorFieldLabel ??
                    t('translation:pleaseFillAllRequiredFields'),
            });
        }
    }, [
        form,
        getFieldLabel,
        layoutFieldIds,
        layoutProcess.blocks,
        notification,
        t,
    ]);

    const handelSubmit = useCallback(
        async ({ extraValues, extraFields, ...action }: SubmitAction) => {
            if (serverErrorFieldNamesRef.current.length) {
                form?.clearErrors(serverErrorFieldNamesRef.current);
                serverErrorFieldNamesRef.current = [];
            }

            const preparedFields = readSignPreparationFields(extraValues);

            const signAction =
                'isSign' in action &&
                'buttonId' in action &&
                action.isSign &&
                action.buttonId &&
                !('type' in action && action.type)
                    ? (action as ServiceButtonAction)
                    : null;

            if (
                signAction &&
                (signAction.needsSignPreparation || signAction.signDocumentId)
            ) {
                const validated = signAction.needsSignPreparation
                    ? await validateForm()
                    : undefined;
                const decision = decideSignFromForm({
                    needsSignPreparation: Boolean(
                        signAction.needsSignPreparation,
                    ),
                    validatedFields: validated
                        ? { ...validated, ...extraFields }
                        : undefined,
                });

                if (decision.type === 'invalid') return;

                signRef.current?.trigger(
                    {
                        ...signAction,
                        serviceId: service?.id,
                        ...(decision.type === 'prepare'
                            ? { signPreparationFields: decision.fields }
                            : {}),
                    },
                    signAction.signDocumentId,
                    {
                        mobileSignIntent:
                            signAction.buttonType === 'Approve'
                                ? 'ApprovalDecisionSign'
                                : 'ServiceDocumentSign',
                    },
                );
                return;
            }

            const data = preparedFields ?? (await validateForm());

            if (data && service?.id) {
                refDiv?.current?.dispatchEvent(
                    new CustomEvent('onSubmitLayout', {
                        detail: {
                            serviceId: service.id,
                            ...extraValues,
                            ...action,
                            fields: preparedFields ?? {
                                ...data,
                                ...extraFields,
                            },
                        },
                        bubbles: true,
                        composed: true,
                    }),
                );
            }
        },
        [form, service?.id, validateForm],
    );

    useEffect(() => {
        const rootNode = refDiv.current?.getRootNode();
        const hostElement = (
            rootNode instanceof ShadowRoot
                ? rootNode.host
                : refDiv.current?.closest('layout-vertical-preview')
        ) as
            | (HTMLElement & {
                  submitAction?: (
                      action: (typeof autoMakeBtns)[0],
                  ) => Promise<void>;
                  showPreview?: (documentId: string) => Promise<void>;
                  setServerValidationErrors?: (
                      errors: WebComponentServerValidationError[],
                  ) => void;
                  clearServerValidationErrors?: () => void;
              })
            | null;

        if (!hostElement) return;

        hostElement.submitAction = async (action) => {
            await handelSubmit(action);
        };
        hostElement.showPreview = async (documentId) => {
            if (!documentId) return;
            previewDocRef.current?.trigger(documentId);
        };
        hostElement.setServerValidationErrors = (errors) => {
            if (!form) return;

            if (serverErrorFieldNamesRef.current.length) {
                form.clearErrors(serverErrorFieldNamesRef.current);
            }

            serverErrorFieldNamesRef.current = applyServerValidationErrors({
                errors,
                form,
                getFieldLabel,
                notification,
                t,
            });
        };
        hostElement.clearServerValidationErrors = () => {
            if (!form || serverErrorFieldNamesRef.current.length === 0) return;
            form.clearErrors(serverErrorFieldNamesRef.current);
            serverErrorFieldNamesRef.current = [];
        };
    }, [form, getFieldLabel, handelSubmit, notification, t, validateForm]);

    useEffect(() => {
        if (!form) return;

        if (serverErrorFieldNamesRef.current.length) {
            form.clearErrors(serverErrorFieldNamesRef.current);
            serverErrorFieldNamesRef.current = [];
        }

        if (!serverValidationErrors?.length) return;

        serverErrorFieldNamesRef.current = applyServerValidationErrors({
            errors: serverValidationErrors,
            form,
            getFieldLabel,
            notification,
            t,
        });
    }, [form, getFieldLabel, notification, serverValidationErrors, t]);

    return (
        <div ref={refDiv}>
            <ShadcnForm
                defaultValues={service?.fieldValues}
                mode='onChange'
                key={service?.currentStageId}
                onForm={handleForm}
                syncToInitialValues
            >
                <LayoutViewVertical
                    classNameWrapper='gap-0!'
                    fileUploadController={fileUploadController}
                    flowTabType='verticalList'
                    layoutProcess={layoutProcess}
                    service={service}
                    tabProps={tabProps}
                />
            </ShadcnForm>
            <PreviewDocument ref={previewDocRef} />
            <SignFlow
                ref={signRef}
                mobileSignServiceId={service?.id}
                mobileSignUrls={mobileSignUrls}
                removeMobileSignSessionId={removeMobileSignSessionId}
                servicesController={fileUploadController ?? 'flow/Services'}
                onSignPreparationSaved={(fields) => {
                    if (!form) return;
                    form.reset(
                        formValuesAfterPreparationSaved(
                            form.getValues(),
                            fields,
                        ),
                    );
                }}
                onAfterSign={(extraData) => {
                    const data = {
                        buttonId: extraData?.buttonId ?? '',
                        extraValues: extraData,
                    };
                    return handelSubmit(data);
                }}
            />
        </div>
    );
};
